import React, { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Home", href: "#top" },
  { label: "Services", href: "#services" },
  { label: "About Us", href: "#about" },
  { label: "Our Work", href: "#portfolio" },
  { label: "Areas We Serve", href: "#areas" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Nav({ site }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-gray-200/70 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.08)]"
          : "bg-white/85 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 sm:h-24 flex items-center justify-between gap-6">
        {/* LEFT — logo */}
        <a href="#top" data-testid="nav-logo-link" className="shrink-0">
          <Logo size={64} className="sm:scale-110" />
        </a>

        {/* CENTER — nav links (desktop) */}
        <nav
          className="hidden xl:flex items-center gap-7 flex-1 justify-center"
          data-testid="nav-links"
        >
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[13px] font-semibold text-gray-800 hover:text-[#B8941F] transition-colors tracking-wide whitespace-nowrap"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* RIGHT — CTA (desktop / tablet) */}
        <a
          href="#contact"
          data-testid="nav-cta-btn"
          className="hidden sm:inline-flex btn-gold !py-3 !px-5 !text-[11px] tracking-wider shrink-0"
        >
          Get Free Estimate
        </a>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
          className="xl:hidden sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#D4AF37]/40 text-[#0A0A0A] hover:bg-[#FBF8F1] transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Tablet (≥sm <xl) hamburger sits beside the CTA */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-tablet-toggle"
          className="hidden sm:inline-flex xl:hidden items-center justify-center w-10 h-10 rounded-full border border-[#D4AF37]/40 text-[#0A0A0A] hover:bg-[#FBF8F1] transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile / tablet dropdown panel */}
      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="xl:hidden border-t border-gray-200/80 bg-white/98 backdrop-blur-xl shadow-[0_12px_32px_-12px_rgba(0,0,0,0.12)]"
        >
          <nav className="max-w-7xl mx-auto px-6 py-5 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-3 px-2 text-[15px] font-semibold text-gray-800 hover:text-[#B8941F] border-b border-gray-100 last:border-b-0 transition-colors"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="sm:hidden mt-3 btn-gold w-full justify-center !text-xs"
            >
              Get Free Estimate
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
